/**
 * Swoop 3-step email popup.
 * Step 1 still posts the email through the Shopify newsletter form.
 * When a Klaviyo list ID is set, that same email is subscribed to the list
 * with the public client API. Step 2 writes the chosen answer onto the profile.
 */
(function () {
  if (window.self !== window.top) return;
  if (window.__swoopEmailPopupJs) return;
  window.__swoopEmailPopupJs = true;
  var STORAGE_KEY = 'swoop:email-popup:v1';
  var KLAVIYO_REVISION = '2025-10-15';

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function readStorage() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') || {};
    } catch (err) {
      return {};
    }
  }

  function writeStorage(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      /* private mode */
    }
  }

  function daysSince(ts) {
    if (!ts) return Infinity;
    return (Date.now() - Number(ts)) / 86400000;
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  function klaviyoHeaders() {
    return {
      accept: 'application/vnd.api+json',
      'content-type': 'application/vnd.api+json',
      revision: KLAVIYO_REVISION
    };
  }

  function detectKlaviyoCompanyId(explicit) {
    var id = String(explicit || '').trim();
    if (id) return id;
    try {
      var scripts = document.querySelectorAll('script[src*="klaviyo"]');
      for (var i = 0; i < scripts.length; i++) {
        var src = scripts[i].getAttribute('src') || '';
        var match = src.match(/[?&]company_id=([A-Za-z0-9]+)/i);
        if (match && match[1]) return match[1];
      }
    } catch (err) {
      /* ignore */
    }
    return '';
  }

  function klaviyoOk(res) {
    return res && (res.ok || res.status === 202);
  }

  function klaviyoFetch(url, body) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? window.setTimeout(function () { controller.abort(); }, 4000) : 0;
    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: klaviyoHeaders(),
      body: JSON.stringify(body),
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      window.clearTimeout(timer);
      if (!klaviyoOk(res)) throw new Error('klaviyo-' + res.status);
      return res;
    }, function (err) {
      window.clearTimeout(timer);
      throw err;
    });
  }

  function klaviyoRetry(run) {
    return run().catch(function () {
      return run();
    });
  }

  function klaviyoCompany(cfg) {
    return detectKlaviyoCompanyId(cfg && cfg.companyId);
  }

  function klaviyoProfile(cfg, email, props) {
    var companyId = klaviyoCompany(cfg);
    if (!companyId || !email) return Promise.resolve({ skipped: true });

    return klaviyoFetch(
      'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(companyId),
      {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: props || {}
          }
        }
      }
    );
  }

  function klaviyoSubscribe(cfg, email) {
    var companyId = klaviyoCompany(cfg);
    var listId = String((cfg && cfg.listId) || '').trim();
    if (!companyId || !listId || !email) return Promise.resolve({ skipped: true });

    return klaviyoFetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId),
      {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Swoop email popup',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: {
                    email: {
                      marketing: { consent: 'SUBSCRIBED' }
                    }
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: { type: 'list', id: listId }
            }
          }
        }
      }
    );
  }

  function syncKlaviyo(cfg, email, props) {
    if (!email) return Promise.resolve();
    identifyKlaviyo(email, props);
    return klaviyoRetry(function () {
      return klaviyoSubscribe(cfg, email);
    }).catch(function (err) {
      console.warn('[swoop-email-popup]', err && err.message ? err.message : err);
    }).then(function () {
      return klaviyoRetry(function () {
        return klaviyoProfile(cfg, email, props);
      });
    }).catch(function (err) {
      console.warn('[swoop-email-popup]', err && err.message ? err.message : err);
    });
  }

  function identifyKlaviyo(email, props) {
    var payload = Object.assign({ email: email, $email: email }, props || {});
    try {
      if (window.klaviyo) {
        if (typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify(payload);
        } else if (typeof window.klaviyo.push === 'function') {
          window.klaviyo.push(['identify', payload]);
        }
      }
    } catch (err) {
      /* ignore */
    }
    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', payload]);
    } catch (err) {
      /* ignore */
    }
  }

  function shouldSuppress(cfg) {
    if (cfg.designMode || cfg.testMode) return false;
    if (cfg.hideLoggedIn && cfg.loggedIn) return true;
    if (cfg.hideSubscribed && cfg.acceptsMarketing) return true;

    var stored = readStorage();
    if (stored.status === 'subscribed') return true;
    if (stored.status === 'dismissed') {
      var wait = Number(cfg.reappearDays);
      if (!wait || wait <= 0) return true;
      return daysSince(stored.at) < wait;
    }
    return false;
  }

  function markCustomerPosted() {
    try {
      if (/[?&]customer_posted=true(?:&|$)/.test(window.location.search)) {
        writeStorage({ status: 'subscribed', at: Date.now(), source: 'shopify-form' });
      }
    } catch (err) {
      /* ignore */
    }
  }

  function init(root) {
    if (!root || root.dataset.swoopEmailPopupBound === 'true') return;
    root.dataset.swoopEmailPopupBound = 'true';

    var dialog = qs(root, 'dialog');
    if (!dialog) return;

    var cfg = {
      enabled: root.getAttribute('data-enabled') === 'true',
      designMode: root.getAttribute('data-design-mode') === 'true' || Boolean(window.Shopify && window.Shopify.designMode),
      testMode: root.getAttribute('data-test-mode') === 'true',
      delayMs: Math.max(0, Number(root.getAttribute('data-delay-seconds') || 0) * 1000),
      scrollPercent: Math.max(0, Math.min(100, Number(root.getAttribute('data-scroll-percent') || 0))),
      triggerDelay: root.getAttribute('data-trigger-delay') === 'true',
      triggerScroll: root.getAttribute('data-trigger-scroll') === 'true',
      hideLoggedIn: root.getAttribute('data-hide-logged-in') === 'true',
      hideSubscribed: root.getAttribute('data-hide-subscribed') === 'true',
      loggedIn: root.getAttribute('data-logged-in') === 'true',
      acceptsMarketing: root.getAttribute('data-accepts-marketing') === 'true',
      reappearDays: Number(root.getAttribute('data-reappear-days') || 0),
      previewStep: root.getAttribute('data-preview-step') || '1',
      companyId: (root.getAttribute('data-klaviyo-company') || '').trim(),
      listId: (root.getAttribute('data-klaviyo-list') || '').trim(),
      problemProperty: (root.getAttribute('data-problem-property') || 'swoop_problem').trim() || 'swoop_problem'
    };

    try {
      if (/[?&](?:ep=modal|swoop_ep=1)(?:&|$)/.test(window.location.search)) {
        cfg.testMode = true;
      }
    } catch (err) {
      /* ignore */
    }

    markCustomerPosted();

    var form = qs(root, '.swoop-email-popup__form');
    var emailInput = qs(root, '[data-swoop-ep-email]');
    var errorEl = qs(root, '[data-swoop-ep-error]');
    var continueBtn = qs(root, '[data-swoop-ep-continue]');
    var frame = qs(root, '[data-swoop-ep-frame]');
    var steps = qsa(root, '[data-swoop-ep-step]');
    var capturedEmail = '';
    var opened = false;
    var delayTimer = 0;
    var postedToShopify = false;
    var advancedFromSignup = false;
    var signupTimer = 0;
    var klaviyoSignup = Promise.resolve();

    if (form && frame) {
      form.setAttribute('target', frame.getAttribute('name'));
    }

    function setError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    function setBusy(busy) {
      if (continueBtn) {
        continueBtn.disabled = busy;
        continueBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
      qsa(root, '[data-swoop-ep-option]').forEach(function (btn) {
        btn.disabled = busy;
      });
      var otherField = qs(root, '[data-swoop-ep-other]');
      var claim = qs(root, '[data-swoop-ep-claim]');
      if (otherField) otherField.disabled = busy;
      if (claim) claim.disabled = busy;
    }

    function showStep(step) {
      var id = String(step);
      steps.forEach(function (panel) {
        var on = panel.getAttribute('data-swoop-ep-step') === id;
        panel.hidden = !on;
      });
      dialog.setAttribute('data-current-step', id);
      if (id === '1' && emailInput) {
        window.setTimeout(function () {
          try {
            emailInput.focus();
          } catch (err) {
            /* ignore */
          }
        }, 80);
      }
    }

    function openPopup() {
      if (opened || dialog.open) return;
      opened = true;
      showStep(cfg.designMode ? cfg.previewStep : '1');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    function closePopup() {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }

    function dismiss(status) {
      if (!cfg.designMode) {
        writeStorage({ status: status || 'dismissed', at: Date.now() });
      }
      closePopup();
    }

    function armTriggers() {
      if (!cfg.enabled && !cfg.designMode && !cfg.testMode) return;
      if (cfg.designMode) {
        openPopup();
        return;
      }
      if (shouldSuppress(cfg)) return;

      var fired = false;
      function fire() {
        if (fired) return;
        fired = true;
        window.clearTimeout(delayTimer);
        window.removeEventListener('scroll', onScroll);
        openPopup();
      }

      if (cfg.testMode || (!cfg.triggerDelay && !cfg.triggerScroll)) {
        fire();
        return;
      }

      if (cfg.triggerDelay) {
        delayTimer = window.setTimeout(fire, cfg.delayMs);
      }

      function onScroll() {
        var doc = document.documentElement;
        var max = doc.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        var pct = (window.scrollY / max) * 100;
        if (pct >= cfg.scrollPercent) fire();
      }

      if (cfg.triggerScroll) {
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }

    qsa(root, '[data-swoop-ep-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        dismiss('dismissed');
      });
    });

    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      dismiss('dismissed');
    });

    function afterShopifySignup() {
      if (advancedFromSignup) return;
      advancedFromSignup = true;
      postedToShopify = false;
      window.clearTimeout(signupTimer);
      writeStorage({ status: 'subscribed', at: Date.now(), email: capturedEmail });
      klaviyoSignup = syncKlaviyo(cfg, capturedEmail, { swoop_signup_source: 'email_popup' });
      showStep('2');
      setBusy(false);
    }

    if (form) {
      form.setAttribute('novalidate', 'novalidate');
      form.addEventListener('submit', function (event) {
        var email = emailInput ? emailInput.value.trim() : '';
        if (!validEmail(email)) {
          event.preventDefault();
          setError(root.getAttribute('data-error-email') || 'Enter a valid email.');
          if (emailInput) emailInput.focus();
          return;
        }

        if (!frame || !form.getAttribute('target')) {
          event.preventDefault();
          setError(root.getAttribute('data-error-generic') || 'Something went wrong. Try again.');
          return;
        }

        setError('');
        capturedEmail = email;
        postedToShopify = true;
        advancedFromSignup = false;
        window.clearTimeout(signupTimer);
        signupTimer = window.setTimeout(afterShopifySignup, 1600);
        window.setTimeout(function () {
          setBusy(true);
        }, 0);
      });
    }

    if (frame) {
      frame.addEventListener('load', function () {
        if (!postedToShopify) return;
        afterShopifySignup();
      });
    }

    function submitProblem(label) {
      if (!capturedEmail && emailInput) capturedEmail = emailInput.value.trim();

      var props = { swoop_signup_source: 'email_popup', swoop_popup_step: 'problem' };
      props[cfg.problemProperty] = label;

      if (!capturedEmail) {
        showStep('3');
        return;
      }

      setBusy(true);
      identifyKlaviyo(capturedEmail, props);
      klaviyoSignup.catch(function () {}).then(function () {
        return klaviyoRetry(function () {
          return klaviyoProfile(cfg, capturedEmail, props);
        });
      }).catch(function (err) {
        console.warn('[swoop-email-popup]', err && err.message ? err.message : err);
      }).then(function () {
        setBusy(false);
        showStep('3');
      });
    }

    qsa(root, '[data-swoop-ep-option]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var label = (btn.getAttribute('data-value') || btn.textContent || '').trim();
        if (!label) return;
        submitProblem(label);
      });
    });

    var otherInput = qs(root, '[data-swoop-ep-other]');
    var claimBtn = qs(root, '[data-swoop-ep-claim]');

    function submitOther() {
      var value = otherInput ? otherInput.value.trim() : '';
      if (!value) {
        if (otherInput) otherInput.focus();
        return;
      }
      submitProblem(value);
    }

    if (claimBtn) {
      claimBtn.addEventListener('click', function (event) {
        event.preventDefault();
        submitOther();
      });
    }

    if (otherInput) {
      otherInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          submitOther();
        }
      });
    }

    var skip = qs(root, '[data-swoop-ep-skip]');
    if (skip) {
      skip.addEventListener('click', function (event) {
        event.preventDefault();
        writeStorage({ status: 'subscribed', at: Date.now(), email: capturedEmail });
        showStep('3');
      });
    }

    var start = qs(root, '[data-swoop-ep-start]');
    if (start) {
      start.addEventListener('click', function () {
        writeStorage({ status: 'subscribed', at: Date.now(), email: capturedEmail });
        closePopup();
      });
    }

    armTriggers();
  }

  function boot() {
    qsa(document, '[data-swoop-email-popup]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    qsa(event.target, '[data-swoop-email-popup]').forEach(function (node) {
      node.dataset.swoopEmailPopupBound = '';
      init(node);
    });
  });
})();
